// ── ProductImagesSection.jsx ──────────────────────────────────────────────────
// Left column: main image/video display, thumbnails, views counter
// ✅ FIX 1: Videos show a real thumbnail frame (not blank grey box)
// ✅ FIX 2: Views are real — POST /api/ads/<id>/view/ on modal open (per-user dedup)
// ✅ FIX 3: via.placeholder.com → offline-safe inline SVG (works on 192.168.x.x)
// ✅ FIX 4: Videos always sorted to the FRONT of the media list
// ✅ FIX 5: Autoplays muted; speaker unmutes at 15% volume (not 100%)

import React, { useEffect, useRef, useState } from 'react';
import styles from '../ProductDetailModal.module.css';

const FALLBACK_IMG_600 =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='400' " +
  "viewBox='0 0 600 400'%3E%3Crect width='600' height='400' fill='%23f0f0f0'/%3E" +
  "%3Crect x='220' y='110' width='160' height='120' rx='8' fill='%23ccc'/%3E" +
  "%3Ccircle cx='260' cy='145' r='18' fill='%23aaa'/%3E" +
  "%3Cpolygon points='220,230 272,175 308,200 344,160 380,230' fill='%23bbb'/%3E" +
  "%3Ctext x='300' y='290' font-family='Arial' font-size='16' fill='%23999' " +
  "text-anchor='middle'%3ENo Image Available%3C/text%3E%3C/svg%3E";

const FALLBACK_IMG_100 =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' " +
  "viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E" +
  "%3Crect x='30' y='22' width='40' height='32' rx='3' fill='%23ccc'/%3E" +
  "%3Ccircle cx='40' cy='34' r='5' fill='%23aaa'/%3E" +
  "%3Cpolygon points='30,54 43,40 53,47 63,35 72,54' fill='%23bbb'/%3E" +
  "%3Ctext x='50' y='72' font-family='Arial' font-size='8' fill='%23999' " +
  "text-anchor='middle'%3ENo Image%3C/text%3E%3C/svg%3E";

const onThumbImgError = (e) => { if (e.target.src !== FALLBACK_IMG_100) e.target.src = FALLBACK_IMG_100; };

// ─── Constants ────────────────────────────────────────────────────────────────
const API_BASE    = process.env.REACT_APP_API_URL || 'https://imselling-marketplace-production.up.railway.app/api';
const SERVER_BASE = import.meta.env?.VITE_SERVER_BASE  || 'https://imselling-marketplace-production.up.railway.app';

function getToken() {
  return localStorage.getItem('authToken') || localStorage.getItem('token');
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const VIDEO_EXTS = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
const isVideoFile = (url) =>
  Boolean(url) && VIDEO_EXTS.some(ext => url.toLowerCase().includes(ext));

const resolveUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) return url;
  if (url.startsWith('/')) return `${SERVER_BASE}${url}`;
  return `${SERVER_BASE}/${url}`;
};

// ✅ FIX 4: Videos always come first — images follow in their original order
const sortVideosFirst = (urls) => [
  ...urls.filter(isVideoFile),
  ...urls.filter(url => !isVideoFile(url)),
];

// ─── VideoThumbnail ───────────────────────────────────────────────────────────
function VideoThumbnail({ src, className, style, onClick, isActive, onCapture }) {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);
  const [failed,  setFailed]  = useState(false);

  useEffect(() => {
    setDataUrl(null);
    setFailed(false);
    const video = videoRef.current;
    if (!video) return;

    const capture = () => {
      try {
        const canvas  = canvasRef.current;
        canvas.width  = video.videoWidth  || 160;
        canvas.height = video.videoHeight || 90;
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        const url = canvas.toDataURL('image/jpeg', 0.8);
        setDataUrl(url);
        if (onCapture) onCapture(src, url);
      } catch {
        setFailed(true);
      }
    };

    const onSeeked = () => capture();
    const onError  = () => setFailed(true);
    const onMeta   = () => { video.currentTime = 0.5; };

    video.addEventListener('seeked',         onSeeked);
    video.addEventListener('error',          onError);
    video.addEventListener('loadedmetadata', onMeta);

    return () => {
      video.removeEventListener('seeked',         onSeeked);
      video.removeEventListener('error',          onError);
      video.removeEventListener('loadedmetadata', onMeta);
    };
  }, [src]);

  return (
    <div className={className} style={{ position: 'relative', ...style }} onClick={onClick}>
      <video ref={videoRef} src={src} muted preload="metadata" style={{ display: 'none' }} />
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {dataUrl ? (
        <img src={dataUrl} alt="video thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
      ) : failed ? (
        <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#1a1a2e,#16213e)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'inherit' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="rgba(255,255,255,0.35)">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
      ) : (
        <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#222,#333)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'inherit' }}>
          <div style={{ width: 20, height: 20, border: '2px solid rgba(255,255,255,0.2)', borderTopColor: 'rgba(255,255,255,0.6)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        </div>
      )}

      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: isActive ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.15)',
        borderRadius: 'inherit',
        pointerEvents: 'none',
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: isActive ? 'rgba(37,211,102,0.85)' : 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
        </div>
      </div>
    </div>
  );
}

// ─── useAdView ────────────────────────────────────────────────────────────────
function useAdView(productId, initialViews) {
  const [views, setViews] = useState(initialViews ?? 0);

  useEffect(() => {
    if (!productId) return;

    const token = getToken();
    let userKey;
    if (token) {
      userKey = token.slice(-12);
    } else {
      let guestId = localStorage.getItem('_gid');
      if (!guestId) {
        guestId = Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem('_gid', guestId);
      }
      userKey = guestId;
    }

    const sessionKey = `viewed_${productId}_${userKey}`;
    if (sessionStorage.getItem(sessionKey)) return;

    let cancelled = false;

    const record = async () => {
      try {
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Token ${token}`;
        const res  = await fetch(`${API_BASE}/ads/${productId}/view/`, { method: 'POST', headers });
        const data = await res.json().catch(() => ({}));
        if (!cancelled) {
          sessionStorage.setItem(sessionKey, '1');
          if (typeof data.views === 'number') setViews(data.views);
        }
      } catch {
        // silently ignore — views counter is non-critical
      }
    };

    record();
    return () => { cancelled = true; };
  }, [productId]);

  return views;
}

// ─── ProductImagesSection ─────────────────────────────────────────────────────
const ProductImagesSection = ({
  product,
  productImages: rawImages,
  activeImageIndex,
  setActiveImageIndex,
  isLiked,
  isLiking,
  likes,
  onLikeToggle,
  relatedProducts,
}) => {
  // ✅ FIX 4: Videos are sorted to the front before resolving URLs.
  const productImages = React.useMemo(
    () => sortVideosFirst(rawImages || []).map(resolveUrl),
    [rawImages]
  );

  const currentImage   = productImages[activeImageIndex] || productImages[0];
  const isCurrentVideo = isVideoFile(currentImage);
  const views          = useAdView(product?.id, product?.views);

  // Poster frames captured by VideoThumbnail components via onCapture callback
  const [videoPosters, setVideoPosters] = useState({});
  const handleCapture = React.useCallback((src, dataUrl) => {
    setVideoPosters(prev => prev[src] ? prev : { ...prev, [src]: dataUrl });
  }, []);

  // Ref to the main video element so we can call .play() programmatically
  const mainVideoRef = useRef(null);
  // Track whether the current video is still buffering
  const [videoLoading, setVideoLoading] = useState(true);
  // Reset loading state whenever the active video changes
  React.useEffect(() => {
    if (isCurrentVideo) setVideoLoading(true);
  }, [currentImage, isCurrentVideo]);

  // Force play programmatically — autoPlay attribute alone can be blocked
  // by the browser if the page hasn't been interacted with yet.
  React.useEffect(() => {
    if (!isCurrentVideo) return;
    const vid = mainVideoRef.current;
    if (!vid) return;
    const tryPlay = () => {
      vid.play().catch(() => {
        // Browser blocked autoplay — video stays paused, user can click play
      });
    };
    // Try immediately, and also on canplay in case video isn't ready yet
    tryPlay();
    vid.addEventListener('canplay', tryPlay, { once: true });
    return () => vid.removeEventListener('canplay', tryPlay);
  }, [currentImage, isCurrentVideo]);

  return (
    <div className={styles.productImagesSection}>
      <div className={styles.imageTopIcons}>
        {/* Share button */}
        <button className={styles.shareBtn} title="Share">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.12549 15.0077 5.24919 15.0227 5.37063L8.08259 9.16534C7.54305 8.46079 6.72364 8 5.8 8C4.14315 8 3 9.34315 3 11C3 12.6569 4.14315 14 5.8 14C6.72364 14 7.54305 13.5392 8.08259 12.8347L15.0227 16.6294C15.0077 16.7508 15 16.8745 15 17C15 18.6569 16.3431 20 18 20C19.6569 20 21 18.6569 21 17C21 15.3431 19.6569 14 18 14C17.0764 14 16.257 14.4608 15.7174 15.1653L8.77735 11.3706C8.79229 11.2492 8.8 11.1255 8.8 11C8.8 10.8745 8.79229 10.7508 8.77735 10.6294L15.7174 6.83466C16.257 7.53921 17.0764 8 18 8Z"
              fill="currentColor"
            />
          </svg>
        </button>

        {/* Like button */}
        <button
          className={`${styles.likeBtn} ${isLiked ? styles.liked : ''}`}
          onClick={onLikeToggle}
          disabled={isLiking}
          title={isLiked ? 'Unlike' : 'Like'}
        >
          <svg
            width="20" height="20" viewBox="0 0 24 24"
            fill={isLiked ? 'currentColor' : 'none'}
            stroke="currentColor" strokeWidth="2"
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
          <span className={styles.likeCount}>{likes}</span>
        </button>
      </div>

      {/* ── Main image / video ── */}
      <div className={styles.mainImageContainer}>
        {isCurrentVideo ? (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <video
              ref={mainVideoRef}
              key={`${product.id}-video-${activeImageIndex}`}
              className={styles.mainProductVideo}
              controls
              autoPlay
              loop
              playsInline
              muted
              preload="auto"
              poster={videoPosters[currentImage] || undefined}
              style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#111' }}
              onCanPlay={(e) => {
                setVideoLoading(false);
                // ✅ FIX 5: Autoplays muted. Briefly unmute→set 15% volume→re-mute
                // all in one sync tick so no sound escapes. When user clicks
                // the speaker icon it unmutes at 15%, not 100%.
                const v = e.currentTarget;
                v.muted  = false;
                v.volume = 0.15;
                v.muted  = true;
              }}
              onPlaying={() => setVideoLoading(false)}
              onWaiting={() => setVideoLoading(true)}
              onStalled={() => setVideoLoading(true)}
            >
              <source src={currentImage} type="video/mp4" />
              <source src={currentImage} type="video/webm" />
              Your browser does not support the video tag.
            </video>

            {/* Loading overlay — shown while buffering, hidden once playing */}
            {videoLoading && !videoPosters[currentImage] && (
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 12,
                borderRadius: 'inherit', pointerEvents: 'none',
              }}>
                <div style={{
                  width: 44, height: 44,
                  border: '3px solid rgba(255,255,255,0.12)',
                  borderTopColor: 'rgba(255,255,255,0.75)',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, letterSpacing: '0.02em' }}>
                  Loading video...
                </span>
              </div>
            )}
          </div>
        ) : (
          <img
            key={`${product.id}-${activeImageIndex}`}
            src={currentImage || FALLBACK_IMG_600}
            alt={product.name}
            className={styles.mainProductImage}
            onError={(e) => {
              const next = productImages.findIndex(
                (url, i) => i > activeImageIndex && (isVideoFile(url) || url !== currentImage)
              );
              if (next !== -1) {
                setActiveImageIndex(next);
              } else {
                e.target.onerror = null;
                e.target.src = FALLBACK_IMG_600;
              }
            }}
          />
        )}
        {productImages.length > 1 && (
          <div className={styles.imageCounter}>
            {activeImageIndex + 1} / {productImages.length}
          </div>
        )}
      </div>

      {/* ── Thumbnails ── */}
      {productImages.length > 1 && (
        <div className={styles.productThumbnails}>
          {productImages.map((media, index) => {
            const isVideo  = isVideoFile(media);
            const isActive = index === activeImageIndex;

            return isVideo ? (
              <VideoThumbnail
                key={`${product.id}-thumb-${index}`}
                src={media}
                isActive={isActive}
                className={`${styles.thumbnail} ${isActive ? styles.active : ''}`}
                onClick={() => setActiveImageIndex(index)}
                onCapture={handleCapture}
              />
            ) : (
              <div
                key={`${product.id}-thumb-${index}`}
                className={`${styles.thumbnail} ${isActive ? styles.active : ''}`}
                onClick={() => setActiveImageIndex(index)}
              >
                <img
                  src={media || FALLBACK_IMG_100}
                  alt={`${product.name} - Image ${index + 1}`}
                  onError={onThumbImgError}
                />
              </div>
            );
          })}

          {/* Filler slots to always show 5 thumbnails */}
          {productImages.length < 5 &&
            [...Array(5 - productImages.length)].map((_, i) => (
              <div key={`empty-${i}`} className={styles.thumbnailPlaceholder} />
            ))
          }
        </div>
      )}

      {/* ✅ FIX 2: Live view count from DB */}
      <div className={styles.viewsCounter}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" />
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
        </svg>
        {views.toLocaleString()} {views === 1 ? 'view' : 'views'}
      </div>

      {relatedProducts?.length > 0 && (
        <div className={styles.similarItemsLink}>
          <a href="#similar-items">{relatedProducts.length} more similar items</a>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ProductImagesSection;













// // ── ProductImagesSection.jsx ──────────────────────────────────────────────────
// // Left column: main image/video display, thumbnails, views counter
// // ✅ FIX 1: Videos show a real thumbnail frame (not blank grey box)
// // ✅ FIX 2: Views are real — POST /api/ads/<id>/view/ on modal open (per-user dedup)
// // ✅ FIX 3: via.placeholder.com → offline-safe inline SVG (works on 192.168.x.x)
// // ✅ FIX 4: Videos always sorted to the FRONT of the media list

// import React, { useEffect, useRef, useState } from 'react';
// import styles from '../ProductDetailModal.module.css';

// const FALLBACK_IMG_600 =
//   "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='400' " +
//   "viewBox='0 0 600 400'%3E%3Crect width='600' height='400' fill='%23f0f0f0'/%3E" +
//   "%3Crect x='220' y='110' width='160' height='120' rx='8' fill='%23ccc'/%3E" +
//   "%3Ccircle cx='260' cy='145' r='18' fill='%23aaa'/%3E" +
//   "%3Cpolygon points='220,230 272,175 308,200 344,160 380,230' fill='%23bbb'/%3E" +
//   "%3Ctext x='300' y='290' font-family='Arial' font-size='16' fill='%23999' " +
//   "text-anchor='middle'%3ENo Image Available%3C/text%3E%3C/svg%3E";

// const FALLBACK_IMG_100 =
//   "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' " +
//   "viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E" +
//   "%3Crect x='30' y='22' width='40' height='32' rx='3' fill='%23ccc'/%3E" +
//   "%3Ccircle cx='40' cy='34' r='5' fill='%23aaa'/%3E" +
//   "%3Cpolygon points='30,54 43,40 53,47 63,35 72,54' fill='%23bbb'/%3E" +
//   "%3Ctext x='50' y='72' font-family='Arial' font-size='8' fill='%23999' " +
//   "text-anchor='middle'%3ENo Image%3C/text%3E%3C/svg%3E";

// const onThumbImgError = (e) => { if (e.target.src !== FALLBACK_IMG_100) e.target.src = FALLBACK_IMG_100; };

// // ─── Constants ────────────────────────────────────────────────────────────────
// const API_BASE    = process.env.REACT_APP_API_URL || 'https://imselling-marketplace-production.up.railway.app/api';
// const SERVER_BASE = import.meta.env?.VITE_SERVER_BASE  || 'https://imselling-marketplace-production.up.railway.app';

// function getToken() {
//   return localStorage.getItem('authToken') || localStorage.getItem('token');
// }

// // ─── Helpers ──────────────────────────────────────────────────────────────────
// const VIDEO_EXTS = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
// const isVideoFile = (url) =>
//   Boolean(url) && VIDEO_EXTS.some(ext => url.toLowerCase().includes(ext));

// const resolveUrl = (url) => {
//   if (!url) return url;
//   if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) return url;
//   if (url.startsWith('/')) return `${SERVER_BASE}${url}`;
//   return `${SERVER_BASE}/${url}`;
// };

// // ✅ FIX 4: Videos always come first — images follow in their original order
// const sortVideosFirst = (urls) => [
//   ...urls.filter(isVideoFile),
//   ...urls.filter(url => !isVideoFile(url)),
// ];

// // ─── VideoThumbnail ───────────────────────────────────────────────────────────
// function VideoThumbnail({ src, className, style, onClick, isActive }) {
//   const videoRef  = useRef(null);
//   const canvasRef = useRef(null);
//   const [dataUrl, setDataUrl] = useState(null);
//   const [failed,  setFailed]  = useState(false);

//   useEffect(() => {
//     setDataUrl(null);
//     setFailed(false);
//     const video = videoRef.current;
//     if (!video) return;

//     const capture = () => {
//       try {
//         const canvas  = canvasRef.current;
//         canvas.width  = video.videoWidth  || 160;
//         canvas.height = video.videoHeight || 90;
//         canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
//         setDataUrl(canvas.toDataURL('image/jpeg', 0.8));
//       } catch {
//         setFailed(true);
//       }
//     };

//     const onSeeked = () => capture();
//     const onError  = () => setFailed(true);
//     const onMeta   = () => { video.currentTime = 0.5; };

//     video.addEventListener('seeked',         onSeeked);
//     video.addEventListener('error',          onError);
//     video.addEventListener('loadedmetadata', onMeta);

//     return () => {
//       video.removeEventListener('seeked',         onSeeked);
//       video.removeEventListener('error',          onError);
//       video.removeEventListener('loadedmetadata', onMeta);
//     };
//   }, [src]);

//   return (
//     <div className={className} style={{ position: 'relative', ...style }} onClick={onClick}>
//       <video ref={videoRef} src={src} muted preload="metadata" style={{ display: 'none' }} crossOrigin="anonymous" />
//       <canvas ref={canvasRef} style={{ display: 'none' }} />

//       {dataUrl ? (
//         <img src={dataUrl} alt="video thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
//       ) : failed ? (
//         <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#1a1a2e,#16213e)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'inherit' }}>
//           <svg width="28" height="28" viewBox="0 0 24 24" fill="rgba(255,255,255,0.35)">
//             <path d="M8 5v14l11-7z"/>
//           </svg>
//         </div>
//       ) : (
//         <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#222,#333)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'inherit' }}>
//           <div style={{ width: 20, height: 20, border: '2px solid rgba(255,255,255,0.2)', borderTopColor: 'rgba(255,255,255,0.6)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
//         </div>
//       )}

//       <div style={{
//         position: 'absolute', inset: 0,
//         display: 'flex', alignItems: 'center', justifyContent: 'center',
//         background: isActive ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.15)',
//         borderRadius: 'inherit',
//         pointerEvents: 'none',
//       }}>
//         <div style={{
//           width: 28, height: 28, borderRadius: '50%',
//           background: isActive ? 'rgba(37,211,102,0.85)' : 'rgba(0,0,0,0.55)',
//           display: 'flex', alignItems: 'center', justifyContent: 'center',
//         }}>
//           <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
//         </div>
//       </div>
//     </div>
//   );
// }

// // ─── useAdView ────────────────────────────────────────────────────────────────
// function useAdView(productId, initialViews) {
//   const [views, setViews] = useState(initialViews ?? 0);

//   useEffect(() => {
//     if (!productId) return;

//     const token = getToken();
//     let userKey;
//     if (token) {
//       userKey = token.slice(-12);
//     } else {
//       let guestId = localStorage.getItem('_gid');
//       if (!guestId) {
//         guestId = Math.random().toString(36).slice(2) + Date.now().toString(36);
//         localStorage.setItem('_gid', guestId);
//       }
//       userKey = guestId;
//     }

//     const sessionKey = `viewed_${productId}_${userKey}`;
//     if (sessionStorage.getItem(sessionKey)) return;

//     let cancelled = false;

//     const record = async () => {
//       try {
//         const headers = { 'Content-Type': 'application/json' };
//         if (token) headers['Authorization'] = `Token ${token}`;
//         const res  = await fetch(`${API_BASE}/ads/${productId}/view/`, { method: 'POST', headers });
//         const data = await res.json().catch(() => ({}));
//         if (!cancelled) {
//           sessionStorage.setItem(sessionKey, '1');
//           if (typeof data.views === 'number') setViews(data.views);
//         }
//       } catch {
//         // silently ignore — views counter is non-critical
//       }
//     };

//     record();
//     return () => { cancelled = true; };
//   }, [productId]);

//   return views;
// }

// // ─── ProductImagesSection ─────────────────────────────────────────────────────
// const ProductImagesSection = ({
//   product,
//   productImages: rawImages,
//   activeImageIndex,
//   setActiveImageIndex,
//   isLiked,
//   isLiking,
//   likes,
//   onLikeToggle,
//   relatedProducts,
// }) => {
//   // ✅ FIX 4: Videos are sorted to the front before resolving URLs.
//   // This means if a listing has 10 photos + 2 videos, the modal opens
//   // on the first video automatically, and the video thumbnails appear
//   // at the left of the strip rather than buried at the end.
//   const productImages = React.useMemo(
//     () => sortVideosFirst(rawImages || []).map(resolveUrl),
//     [rawImages]
//   );

//   const currentImage   = productImages[activeImageIndex] || productImages[0];
//   const isCurrentVideo = isVideoFile(currentImage);
//   const views          = useAdView(product?.id, product?.views);

//   return (
//     <div className={styles.productImagesSection}>
//       <div className={styles.imageTopIcons}>
//         {/* Share button */}
//         <button className={styles.shareBtn} title="Share">
//           <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
//             <path
//               d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.12549 15.0077 5.24919 15.0227 5.37063L8.08259 9.16534C7.54305 8.46079 6.72364 8 5.8 8C4.14315 8 3 9.34315 3 11C3 12.6569 4.14315 14 5.8 14C6.72364 14 7.54305 13.5392 8.08259 12.8347L15.0227 16.6294C15.0077 16.7508 15 16.8745 15 17C15 18.6569 16.3431 20 18 20C19.6569 20 21 18.6569 21 17C21 15.3431 19.6569 14 18 14C17.0764 14 16.257 14.4608 15.7174 15.1653L8.77735 11.3706C8.79229 11.2492 8.8 11.1255 8.8 11C8.8 10.8745 8.79229 10.7508 8.77735 10.6294L15.7174 6.83466C16.257 7.53921 17.0764 8 18 8Z"
//               fill="currentColor"
//             />
//           </svg>
//         </button>

//         {/* Like button */}
//         <button
//           className={`${styles.likeBtn} ${isLiked ? styles.liked : ''}`}
//           onClick={onLikeToggle}
//           disabled={isLiking}
//           title={isLiked ? 'Unlike' : 'Like'}
//         >
//           <svg
//             width="20" height="20" viewBox="0 0 24 24"
//             fill={isLiked ? 'currentColor' : 'none'}
//             stroke="currentColor" strokeWidth="2"
//           >
//             <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
//           </svg>
//           <span className={styles.likeCount}>{likes}</span>
//         </button>
//       </div>

//       {/* ── Main image / video ── */}
//       <div className={styles.mainImageContainer}>
//         {isCurrentVideo ? (
//           <video
//             key={`${product.id}-video-${activeImageIndex}`}
//             className={styles.mainProductVideo}
//             controls autoPlay loop playsInline
//             ref={(el) => { if (el) { el.muted = true; el.volume = 0.15; } }}
//             style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#f3f4f6' }}
//           >
//             <source src={currentImage} type="video/mp4" />
//             <source src={currentImage} type="video/webm" />
//             Your browser does not support the video tag.
//           </video>
//         ) : (
//           <img
//             key={`${product.id}-${activeImageIndex}`}
//             src={currentImage || FALLBACK_IMG_600}
//             alt={product.name}
//             className={styles.mainProductImage}
//             onError={(e) => {
//               const next = productImages.findIndex(
//                 (url, i) => i > activeImageIndex && (isVideoFile(url) || url !== currentImage)
//               );
//               if (next !== -1) {
//                 setActiveImageIndex(next);
//               } else {
//                 e.target.onerror = null;
//                 e.target.src = FALLBACK_IMG_600;
//               }
//             }}
//           />
//         )}
//         {productImages.length > 1 && (
//           <div className={styles.imageCounter}>
//             {activeImageIndex + 1} / {productImages.length}
//           </div>
//         )}
//       </div>

//       {/* ── Thumbnails ── */}
//       {productImages.length > 1 && (
//         <div className={styles.productThumbnails}>
//           {productImages.map((media, index) => {
//             const isVideo  = isVideoFile(media);
//             const isActive = index === activeImageIndex;

//             return isVideo ? (
//               <VideoThumbnail
//                 key={`${product.id}-thumb-${index}`}
//                 src={media}
//                 isActive={isActive}
//                 className={`${styles.thumbnail} ${isActive ? styles.active : ''}`}
//                 onClick={() => setActiveImageIndex(index)}
//               />
//             ) : (
//               <div
//                 key={`${product.id}-thumb-${index}`}
//                 className={`${styles.thumbnail} ${isActive ? styles.active : ''}`}
//                 onClick={() => setActiveImageIndex(index)}
//               >
//                 <img
//                   src={media || FALLBACK_IMG_100}
//                   alt={`${product.name} - Image ${index + 1}`}
//                   onError={onThumbImgError}
//                 />
//               </div>
//             );
//           })}

//           {/* Filler slots to always show 5 thumbnails */}
//           {productImages.length < 5 &&
//             [...Array(5 - productImages.length)].map((_, i) => (
//               <div key={`empty-${i}`} className={styles.thumbnailPlaceholder} />
//             ))
//           }
//         </div>
//       )}

//       {/* ✅ FIX 2: Live view count from DB */}
//       <div className={styles.viewsCounter}>
//         <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
//           <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" />
//           <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
//         </svg>
//         {views.toLocaleString()} {views === 1 ? 'view' : 'views'}
//       </div>

//       {relatedProducts?.length > 0 && (
//         <div className={styles.similarItemsLink}>
//           <a href="#similar-items">{relatedProducts.length} more similar items</a>
//         </div>
//       )}

//       <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
//     </div>
//   );
// };

// export default ProductImagesSection;



